import React, { useState } from 'react';
import { Sun, Moon, Bell, Menu, ChevronDown, LogOut, User, Settings } from 'lucide-react';

const pageLabels = {
  beranda: 'Beranda',
  tracker: 'Journey Tracker',
  escrow: 'Smart Escrow',
  komunitas: 'Komunitas',
  toko: 'Toko',
};

export default function TopBar({ theme, toggleTheme, onLogout, user, currentPage, setSidebarOpen }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const isDark = theme === 'dark';

  return (
    <header
      data-testid="dashboard-topbar"
      className={`sticky top-0 z-20 h-16 flex items-center px-4 sm:px-6 border-b ${
        isDark
          ? 'bg-[#1A1A24] border-[#2D3748]'
          : 'bg-white border-[#E2E8F0]'
      }`}
    >
      {/* Left: Hamburger + Page Label */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <button
          data-testid="sidebar-toggle-btn"
          onClick={() => setSidebarOpen(prev => !prev)}
          className={`lg:hidden p-2 rounded-xl transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`}
        >
          <Menu size={20} />
        </button>
        <div className="min-w-0">
          <h1 className={`text-base font-bold truncate ${isDark ? 'text-white' : 'text-[#1A202C]'}`}>
            {pageLabels[currentPage] || 'Dashboard'}
          </h1>
          <p className={`text-xs hidden sm:block ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Selamat datang, {user?.name?.split(' ')[0] || 'Jamaah'}!
          </p>
        </div>
      </div>

      {/* Right */}
      <div className="flex items-center gap-2">
        {/* Theme Toggle */}
        <button
          data-testid="theme-toggle-dashboard"
          onClick={toggleTheme}
          className={`p-2 rounded-xl transition-colors ${isDark ? 'text-gray-400 hover:text-[#00C9B1] hover:bg-white/5' : 'text-gray-400 hover:text-[#00C9B1] hover:bg-gray-100'}`}
          title="Toggle Theme"
        >
          {isDark ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        {/* Notifications */}
        <button
          data-testid="notification-btn"
          className={`relative p-2 rounded-xl transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`}
        >
          <Bell size={18} />
          <span className="absolute top-1 right-1 w-2 h-2 bg-[#00C9B1] rounded-full" />
        </button>

        {/* User Dropdown */}
        <div className="relative">
          <button
            data-testid="user-profile-btn"
            onClick={() => setShowDropdown(!showDropdown)}
            className={`flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-xl transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-100'}`}
          >
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#00C9B1] to-[#00A896] flex items-center justify-center text-[#0E0E12] font-bold text-xs">
              {user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'BS'}
            </div>
            <span className={`text-sm font-medium hidden sm:block ${isDark ? 'text-gray-200' : 'text-[#2D3748]'}`}>
              {user?.name?.split(' ')[0] || 'Jamaah'}
            </span>
            <ChevronDown size={14} className={`hidden sm:block ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
          </button>

          {/* Dropdown */}
          {showDropdown && (
            <div
              className={`absolute right-0 top-full mt-2 w-52 rounded-2xl border shadow-xl overflow-hidden z-50 ${
                isDark ? 'bg-[#1A1A24] border-[#2D3748]' : 'bg-white border-[#E2E8F0]'
              }`}
            >
              {/* User Info */}
              <div className={`px-4 py-3 border-b ${isDark ? 'border-[#2D3748]' : 'border-[#E2E8F0]'}`}>
                <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-[#1A202C]'}`}>{user?.name}</p>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{user?.email}</p>
                <div className="flex items-center gap-1.5 mt-2">
                  <div className="w-2 h-2 rounded-full bg-[#00C9B1]" />
                  <span className="text-xs text-[#00C9B1] font-medium">Trust Score: 95</span>
                </div>
              </div>
              {/* Actions */}
              <div className="py-2">
                {[
                  { icon: User, label: 'Profil Saya' },
                  { icon: Settings, label: 'Pengaturan' },
                ].map(item => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.label}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                        isDark ? 'text-gray-300 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-[#1A202C] hover:bg-gray-50'
                      }`}
                    >
                      <Icon size={15} strokeWidth={1.5} />
                      {item.label}
                    </button>
                  );
                })}
                <button
                  data-testid="topbar-logout-btn"
                  onClick={() => { setShowDropdown(false); onLogout(); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-red-400/10 transition-colors"
                >
                  <LogOut size={15} strokeWidth={1.5} />
                  Keluar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Close dropdown on outside click */}
      {showDropdown && (
        <div className="fixed inset-0 z-40" onClick={() => setShowDropdown(false)} />
      )}
    </header>
  );
}
